import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { excerptFromPost, fetchBlogPosts, formatBlogDate, estimateReadTimeMinutes, type BlogPost } from '../lib/blogFirestore';
import Header from '../components/Header';
import Footer from '../components/Footer';

type CategoryKey = 'all' | string;

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await fetchBlogPosts({ siteKey: 'tagIt' });
        if (cancelled) return;
        setPosts(items);
      } catch (err: any) {
        if (cancelled) return;
        const msg = (err?.message || 'Failed to load blog posts.').toString();
        setError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.title = 'Knotiv Journal';
  }, []);

  const catParam = (params.get('category') as CategoryKey | null) ?? null;
  const initialCategory: CategoryKey = catParam || 'all';
  const [active, setActive] = useState<CategoryKey>(initialCategory);
  useEffect(() => {
    if (catParam) setActive(catParam);
  }, [catParam]);

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    for (const post of posts) {
      if (!Array.isArray(post.tags)) continue;
      for (const t of post.tags) {
        const trimmed = (t || '').trim();
        if (trimmed) tags.add(trimmed);
      }
    }
    return Array.from(tags).sort((a, b) => a.localeCompare(b));
  }, [posts]);

  const filtered = useMemo(() => {
    if (active === 'all') return posts;
    return posts.filter((p) => (Array.isArray(p.tags) ? p.tags.includes(active) : false));
  }, [posts, active]);

  const setCategory = (key: CategoryKey) => {
    setActive(key);
    const next = new URLSearchParams(params);
    if (key === 'all') next.delete('category');
    else next.set('category', key);
    setParams(next, { replace: true });
  };

  return (
    <div className="app">
      <Header />
      <main className="section blog">
        <div className="container">
          <div className="blog-head">
            <h1>Knotiv Journal</h1>
            <p className="blog-subhead">
              Practical thinking on governed AI, measurement, and continuous agent improvement.
            </p>
          </div>

          <div className="blog-filters" role="toolbar" aria-label="Blog categories">
            <button
              type="button"
              className={`pill ${active === 'all' ? 'pill-active' : ''}`}
              aria-pressed={active === 'all'}
              onClick={() => setCategory('all')}
            >
              All
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`pill ${active === tag ? 'pill-active' : ''}`}
                aria-pressed={active === tag}
                onClick={() => setCategory(tag)}
              >
                {tag}
              </button>
            ))}
          </div>

          {error && <div className="card blog-empty">Error: {error}</div>}

          <ul className="blog-list">
            {filtered.map((post, idx) => {
              const minutes = estimateReadTimeMinutes(post.content);
              const isFeatured = idx === 0 && filtered.length > 0;
              const topTag = Array.isArray(post.tags) && post.tags.length ? post.tags[0] : null;

              return (
                <li key={post.slug}>
                  <Link to={`/blog/${post.slug}`} className={`blog-card ${isFeatured ? 'blog-card-featured' : ''}`}>
                    {post.featured_image && (
                      <div className={`blog-card-media ${isFeatured ? 'blog-card-media-featured' : ''}`}>
                        <img
                          src={post.featured_image}
                          alt={post.imageAlt || post.title}
                          loading={isFeatured ? 'eager' : 'lazy'}
                        />
                      </div>
                    )}
                    <div className="blog-card-body">
                      <div className="blog-card-meta">
                        {topTag && <span className="badge">{topTag}</span>}
                        <span className="muted">{formatBlogDate(post.date)}</span>
                      </div>
                      <h2 className="blog-card-title">{post.title}</h2>
                      <p className="blog-card-excerpt">{excerptFromPost(post)}</p>
                      <div className="blog-card-footer">
                        {Array.isArray(post.tags) && post.tags.length > 1 && (
                          <span className="muted blog-card-tags">
                            {post.tags.slice(1, 3).join(', ')}
                          </span>
                        )}
                        <span className="muted">{minutes} min read</span>
                      </div>
                      <div className="blog-card-cta">
                        <span className="link">Read more</span>
                        <span aria-hidden="true">→</span>
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>

          {!filtered.length && !error && (
            <div className="card blog-empty">No posts match the selected category.</div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

